import { useEffect, useState, useMemo } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useSystemSettings } from "@/hooks/use-system-settings";
import { formatDistanceToNow } from "date-fns";
import { sk } from "date-fns/locale";
import { 
  CircleAlert, 
  Loader2, 
  CheckCircle, 
  CircleDashed, 
  PlayCircle, 
  Clock, 
  FileCheck, 
  AlertCircle, 
  Calendar, 
  ChevronRight,
  ChevronDown,
  Search,
  Filter,
  Settings,
  Eye,
  EyeOff,
  User,
  MapPin,
  Euro
} from "lucide-react";
import { statusLabel } from "@/shared/statusLabel";

interface SharedClientTasksData {
  client: {
    id: number;
    name: string;
  };
  tasks: Array<{
    id: number;
    title: string;
    description: string | null;
    status: "pending" | "in_progress" | "completed";
    dueDate: string | null;
    createdAt: string;
    isInvoiced: boolean;
    totalAmount?: number;
  }>;
}

type FilterStatus = "all" | "pending" | "in_progress" | "completed";
type SortBy = "date" | "title" | "status" | "due_date";

export default function ClientTasks() {
  const [location] = useLocation();
  const { toast } = useToast();
  const { settings } = useSystemSettings();
  const [clientData, setClientData] = useState<SharedClientTasksData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<FilterStatus>("all");
  const [hideInvoiced, setHideInvoiced] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>("date");
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState<Set<number>>(new Set());

  useEffect(() => {
    const fetchClientTasks = async () => {
      try {
        // Extract client ID from URL (assuming format /client/:id/tasks)
        const clientId = location.split("/")[2];
        if (!clientId || isNaN(parseInt(clientId))) {
          setError("Neplatné ID klienta");
          setIsLoading(false);
          return;
        }

        const response = await apiRequest("GET", `/api/clients/${clientId}/tasks`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError("Klient nebol nájdený");
          } else {
            setError("Chyba pri načítaní údajov");
          }
          setIsLoading(false);
          return;
        }
        
        const data = await response.json();
        setClientData(data);
      } catch (err) {
        console.error("Error loading client tasks:", err);
        setError("Chyba pri načítaní úloh klienta");
      } finally {
        setIsLoading(false);
      }
    };

    fetchClientTasks();
  }, [location]);

  // Helper function to get status badge design
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800">
            <CheckCircle className="h-3.5 w-3.5 mr-1" />
            {statusLabel('completed', 'sk')}
          </Badge>
        );
      case "in_progress":
        return (
          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800">
            <PlayCircle className="h-3.5 w-3.5 mr-1" />
            {statusLabel('in_progress', 'sk')}
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="border-slate-200 dark:border-slate-700">
            <CircleDashed className="h-3.5 w-3.5 mr-1" />
            {statusLabel('pending', 'sk')}
          </Badge>
        );
    }
  };

  // Helper function to get due date formatting
  const getDueDateElement = (dueDate: string | null) => {
    if (!dueDate) return null;

    const date = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dueDateCopy = new Date(date);
    dueDateCopy.setHours(0, 0, 0, 0);
    
    const diffTime = dueDateCopy.getTime() - today.getTime();
    const diffDays = diffTime / (1000 * 3600 * 24);
    
    let textColor = "";
    let icon = <Calendar className="h-4 w-4 mr-1.5" />;
    
    if (diffDays < 0) {
      textColor = "text-red-600 dark:text-red-400";
      icon = <AlertCircle className="h-4 w-4 mr-1.5" />;
    } else if (diffDays === 0) {
      textColor = "text-orange-600 dark:text-orange-400";
      icon = <Clock className="h-4 w-4 mr-1.5" />;
    } else if (diffDays <= 3) {
      textColor = "text-yellow-600 dark:text-yellow-400";
    }
    
    return (
      <div className={`flex items-center text-sm ${textColor}`}>
        {icon}
        <span>Termín: {date.toLocaleDateString()}</span>
        <span className="text-muted-foreground ml-1.5">
          ({formatDistanceToNow(date, { addSuffix: true, locale: sk })})
        </span>
      </div>
    );
  };

  // Filtered and sorted tasks
  const filteredTasks = useMemo(() => {
    if (!clientData) return [];
    
    let filtered = clientData.tasks;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(task => 
        task.title.toLowerCase().includes(query) ||
        (task.description && task.description.toLowerCase().includes(query))
      );
    }
    
    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(task => task.status === statusFilter);
    }
    
    // Apply invoiced filter
    if (hideInvoiced) {
      filtered = filtered.filter(task => !task.isInvoiced);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "title":
          return a.title.localeCompare(b.title);
        case "status":
          return a.status.localeCompare(b.status);
        case "due_date":
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        case "date":
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });
    
    return filtered;
  }, [clientData, searchQuery, statusFilter, hideInvoiced, sortBy]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Načítavanie úloh...</p>
        </div>
      </div>
    );
  }

  if (error || !clientData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-96 border-destructive/20">
          <CardContent className="pt-6 text-center">
            <CircleAlert className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Chyba</h2>
            <p className="text-muted-foreground">{error || "Nepodarilo sa načítať údaje"}</p>
            <Button variant="outline" className="mt-4" onClick={() => window.history.back()}>
              Späť
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { client, tasks } = clientData;
  const pendingTasks = tasks.filter(task => task.status === "pending");
  const inProgressTasks = tasks.filter(task => task.status === "in_progress");
  const completedTasks = tasks.filter(task => task.status === "completed");
  const invoicedTasks = tasks.filter(task => task.isInvoiced);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="w-full py-4 px-4 sm:px-6 border-b bg-card shadow-sm">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-4">
            {settings?.['app.logo']?.value ? (
              <img 
                src={settings['app.logo'].value} 
                alt="Logo firmy" 
                className="h-8 w-auto"
              />
            ) : (
              <h1 className="text-lg font-semibold text-foreground">{settings?.['app.name']?.value || 'TaskkroW'}</h1>
            )}
            <Separator orientation="vertical" className="h-6" />
            <span className="text-sm text-muted-foreground">Prehľad úloh pre klienta</span>
          </div>
          <div className="flex items-center space-x-2">
            <ThemeToggle />
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="flex-1 py-8 px-4 sm:px-6 bg-muted/30">
        <div className="w-full space-y-8">
          {/* Title and description */}
          <div className="text-center">
            <h1 className="text-3xl font-bold text-foreground">Úlohy pre: {client.name}</h1>
            <p className="text-muted-foreground mt-2">
              Prehľad všetkých aktuálnych úloh a ich stavu pre vášho klienta
            </p>
          </div>

          {/* Filters and controls */}
          <Card className="bg-card/50 backdrop-blur-sm border-border/50">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center space-x-2">
                  <Filter className="h-5 w-5" />
                  <span>Filtrovanie a zoradenie</span>
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFiltersVisible(!filtersVisible)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {filtersVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  {filtersVisible ? 'Skryť' : 'Zobraziť'}
                </Button>
              </div>
            </CardHeader>
            {filtersVisible && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Search */}
                  <div className="space-y-2">
                    <Label htmlFor="search" className="text-sm font-medium">Hľadať úlohy</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="search"
                        placeholder="Názov úlohy alebo popis..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  {/* Status filter */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Stav úlohy</Label>
                    <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as FilterStatus)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Všetky stavy</SelectItem>
                        <SelectItem value="pending">{statusLabel('pending', 'sk')}</SelectItem>
                        <SelectItem value="in_progress">{statusLabel('in_progress', 'sk')}</SelectItem>
                        <SelectItem value="completed">{statusLabel('completed', 'sk')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Sort by */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Zoradiť podľa</Label>
                    <Select value={sortBy} onValueChange={(value) => setSortBy(value as SortBy)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="date">Dátum vytvorenia</SelectItem>
                        <SelectItem value="title">Názov úlohy</SelectItem>
                        <SelectItem value="status">Stav úlohy</SelectItem>
                        <SelectItem value="due_date">Termín dokončenia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Hide invoiced */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Možnosti zobrazenia</Label>
                    <div className="flex items-center space-x-2 pt-2">
                      <Checkbox
                        id="hideInvoiced"
                        checked={hideInvoiced}
                        onCheckedChange={(checked) => setHideInvoiced(checked === true)}
                      />
                      <Label htmlFor="hideInvoiced" className="text-sm cursor-pointer">
                        Skryť vyfakturované úlohy
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Filter summary */}
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                      Zobrazuje sa {filteredTasks.length} z {tasks.length} úloh
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSearchQuery("");
                        setStatusFilter("all");
                        setHideInvoiced(false);
                        setSortBy("date");
                      }}
                      className="text-xs"
                    >
                      Vymazať filtre
                    </Button>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
          
          {/* Task summary */}
          <div className="grid gap-4 md:grid-cols-4 mb-8">
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950/20 dark:to-amber-900/20 border-amber-200 dark:border-amber-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center space-x-2 text-amber-800 dark:text-amber-300">
                  <CircleDashed className="h-4 w-4" />
                  <span>Čaká na spracovanie</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-amber-900 dark:text-amber-200">{pendingTasks.length}</div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/20 dark:to-blue-900/20 border-blue-200 dark:border-blue-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center space-x-2 text-blue-800 dark:text-blue-300">
                  <PlayCircle className="h-4 w-4" />
                  <span>Rozpracované</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-900 dark:text-blue-200">{inProgressTasks.length}</div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/20 dark:to-green-900/20 border-green-200 dark:border-green-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center space-x-2 text-green-800 dark:text-green-300">
                  <CheckCircle className="h-4 w-4" />
                  <span>Dokončené</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-900 dark:text-green-200">{completedTasks.length}</div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950/20 dark:to-slate-900/20 border-slate-200 dark:border-slate-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center space-x-2 text-slate-800 dark:text-slate-300">
                  <FileCheck className="h-4 w-4" />
                  <span>Vyfakturované</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-900 dark:text-slate-200">{invoicedTasks.length}</div>
              </CardContent>
            </Card>
          </div>
          
          {filteredTasks.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="flex flex-col items-center space-y-4">
                  <FileCheck className="h-12 w-12 text-muted-foreground/50" />
                  <h3 className="text-xl font-medium">
                    {tasks.length === 0 ? "Žiadne úlohy" : "Žiadne úlohy vyhovujúce filtrom"}
                  </h3>
                  <p className="text-muted-foreground max-w-md">
                    {tasks.length === 0 
                      ? "V súčasnosti nie sú pre vás definované žiadne úlohy."
                      : "Skúste upraviť filtre alebo vyhľadávací dotaz pre zobrazenie úloh."
                    }
                  </p>
                  {tasks.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearchQuery("");
                        setStatusFilter("all");
                        setHideInvoiced(false);
                        setSortBy("date");
                      }}
                      className="mt-2"
                    >
                      Vymazať všetky filtre
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-8">
              {/* Tasks list */}
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold text-foreground">Zoznam úloh</h2>
                  <div className="text-sm text-muted-foreground">
                    {filteredTasks.length} z {tasks.length} úloh
                  </div>
                </div>
                <div className="grid gap-4">
                  {Array.isArray(filteredTasks) && filteredTasks.length > 0 ? filteredTasks.map(task => (
                    <Card key={task.id} className={`overflow-hidden transition-all hover:shadow-md ${
                      task.isInvoiced ? 'bg-muted/30 border-muted' : 'bg-card'
                    }`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <CardTitle className="text-lg font-semibold">{task.title}</CardTitle>
                              {task.isInvoiced && (
                                <Badge variant="secondary" className="text-xs bg-muted text-muted-foreground">
                                  <FileCheck className="h-3 w-3 mr-1" />
                                  Vyfakturované
                                </Badge>
                              )}
                            </div>
                            <CardDescription className="flex items-center space-x-2 text-sm">
                              <span>Úloha #{task.id}</span>
                              <span>•</span>
                              <span>Vytvorená: {new Date(task.createdAt).toLocaleDateString()}</span>
                              {task.totalAmount && (
                                <>
                                  <span>•</span>
                                  <span className="font-medium">{task.totalAmount}€</span>
                                </>
                              )}
                            </CardDescription>
                          </div>
                          <div className="flex flex-col items-end space-y-2">
                            {getStatusBadge(task.status)}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        {task.description && (
                          <div className="text-sm text-muted-foreground mb-4 whitespace-pre-line leading-relaxed">
                            {task.description}
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          {task.dueDate && getDueDateElement(task.dueDate)}
                          <div className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newExpanded = new Set(expandedTasks);
                                if (newExpanded.has(task.id)) {
                                  newExpanded.delete(task.id);
                                } else {
                                  newExpanded.add(task.id);
                                }
                                setExpandedTasks(newExpanded);
                              }}
                              className="text-primary hover:text-primary-foreground hover:bg-primary"
                            >
                              {expandedTasks.has(task.id) ? "Skryť detaily" : "Zobraziť detaily"}
                              {expandedTasks.has(task.id) ? (
                                <ChevronDown className="h-4 w-4 ml-1" />
                              ) : (
                                <ChevronRight className="h-4 w-4 ml-1" />
                              )}
                            </Button>
                          </div>
                        </div>
                        
                        {/* Expandable task details */}
                        {expandedTasks.has(task.id) && (
                          <div className="mt-4 pt-4 border-t border-border/50">
                            <div className="grid gap-4 md:grid-cols-2">
                              <div className="space-y-3">
                                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                                  Základné informácie
                                </h4>
                                <div className="space-y-2">
                                  <div className="flex items-center text-sm">
                                    <User className="h-4 w-4 mr-2 text-muted-foreground" />
                                    <span className="text-muted-foreground">Klient:</span>
                                    <span className="ml-2 font-medium">{clientData?.client.name}</span>
                                  </div>
                                  <div className="flex items-center text-sm">
                                    <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                                    <span className="text-muted-foreground">Vytvorené:</span>
                                    <span className="ml-2">{new Date(task.createdAt).toLocaleString()}</span>
                                  </div>
                                  {task.totalAmount && (
                                    <div className="flex items-center text-sm">
                                      <Euro className="h-4 w-4 mr-2 text-muted-foreground" />
                                      <span className="text-muted-foreground">Suma:</span>
                                      <span className="ml-2 font-medium">{task.totalAmount}€</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="space-y-3">
                                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                                  Stav úlohy
                                </h4>
                                <div className="space-y-2">
                                  <div className="flex items-center text-sm">
                                    <span className="text-muted-foreground">Status:</span>
                                    <span className="ml-2">{getStatusBadge(task.status)}</span>
                                  </div>
                                  {task.dueDate && (
                                    <div className="flex items-center text-sm">
                                      <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                                      <span className="text-muted-foreground">Termín:</span>
                                      <span className="ml-2">{new Date(task.dueDate).toLocaleDateString()}</span>
                                    </div>
                                  )}
                                  <div className="flex items-center text-sm">
                                    <FileCheck className="h-4 w-4 mr-2 text-muted-foreground" />
                                    <span className="text-muted-foreground">Vyfakturované:</span>
                                    <span className="ml-2">{task.isInvoiced ? "Áno" : "Nie"}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {task.description && (
                              <div className="mt-4">
                                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide mb-2">
                                  Popis úlohy
                                </h4>
                                <div className="text-sm bg-muted/30 rounded-lg p-3 whitespace-pre-line">
                                  {task.description}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )) : null}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}